import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Card, Input, Button, Table, Modal, Form, FormItem, DatePicker, Image, Tag, useToast
} from '../ui';
import type { Column } from '../ui';
import { 
  QrCodeIcon, MagnifyingGlassIcon, PlusIcon, EyeIcon,
  ClockIcon, ExclamationTriangleIcon, CheckCircleIcon
} from '@heroicons/react/24/outline';
import { supabase, Dress, Customer, Rental } from '../config/supabase';
import { checkDressAvailability, AvailabilityResult } from '../utils/availability';
import RentalContract from './RentalContract';
import CustomerSelect from './CustomerSelect';

const BarcodeScanner: React.FC = () => {
  const [dress, setDress] = useState<Dress | null>(null);
  const [loading, setLoading] = useState(false);
  const [barcode, setBarcode] = useState('');
  const [scannerInput, setScannerInput] = useState('');
  const scannerInputRef = useRef<HTMLInputElement>(null);
  
  // Rental related states
  const [currentRentals, setCurrentRentals] = useState<Rental[]>([]);
  const [rentalHistory, setRentalHistory] = useState<Rental[]>([]);
  const [, setCustomers] = useState<Customer[]>([]);
  const [rentalModalVisible, setRentalModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  
  // Contract states
  const [showContract, setShowContract] = useState(false);
  const [selectedRental, setSelectedRental] = useState<Rental | null>(null);
  
  // Availability checking states (only for rental form)
  const [rentalAvailability, setRentalAvailability] = useState<AvailabilityResult | null>(null);
  const [isUsingAvailabilityChecker, setIsUsingAvailabilityChecker] = useState(false);
  
  // Quick availability checker dates
  const [quickCheckStartDate, setQuickCheckStartDate] = useState<string>('');
  const [quickCheckEndDate, setQuickCheckEndDate] = useState<string>('');
  
  const { success, error: showError } = useToast();

  const fetchCustomers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('name');

      if (error) throw error;
      setCustomers(data || []);
    } catch (error: any) {
      showError('Error fetching customers: ' + error.message);
    }
  }, [showError]);

  // Focus the scanner input when component mounts
  useEffect(() => {
    if (scannerInputRef.current) {
      scannerInputRef.current.focus();
    }
    fetchCustomers();
  }, [fetchCustomers]);

  // Check availability when both quick check dates are selected
  useEffect(() => {
    const checkQuickAvailability = async () => {
      if (dress && quickCheckStartDate && quickCheckEndDate) {
        console.log('Checking availability for:', { dressId: dress.id, quickCheckStartDate, quickCheckEndDate });
        const availability = await checkDressAvailability(dress.id, quickCheckStartDate, quickCheckEndDate);
        setRentalAvailability(availability);
      } else {
        setRentalAvailability(null);
      }
    };
    
    checkQuickAvailability();
  }, [dress, quickCheckStartDate, quickCheckEndDate]);

  // Keep scanner input focused only when not in a modal or interacting with availability checker or manual search
  const handleScannerInputBlur = () => {
    setTimeout(() => {
      // Don't refocus if any modal is open or if user is actively using availability checker or manual search
      const isModalOpen = rentalModalVisible || historyModalVisible || imageModalVisible;
      const hasModalInDOM = document.querySelector('[role="dialog"], .modal, [class*="modal"]');
      const activeElement = document.activeElement;
      const isUsingManualSearch = activeElement && (
        activeElement.getAttribute('placeholder')?.includes('Enter barcode manually') ||
        activeElement.closest('.manual-search-container')
      );
      
      if (!isModalOpen && !hasModalInDOM && !isUsingAvailabilityChecker && !isUsingManualSearch && scannerInputRef.current) {
        scannerInputRef.current.focus();
      }
    }, 100);
  };

  const searchDressByBarcode = async (searchBarcode: string) => {
    if (!searchBarcode.trim()) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('dresses')
        .select(`
          *,
          brands (
            id,
            name
          ),
          categories (
            id,
            name,
            color
          )
        `)
        .eq('barcode', searchBarcode.trim())
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          showError('No dress found with this barcode');
        } else {
          throw error;
        }
        setDress(null);
        setRentalAvailability(null);
        return;
      }

      setDress(data);
      setBarcode(searchBarcode.trim());
      setRentalAvailability(null); // Clear previous availability results
      setQuickCheckStartDate(''); // Clear quick check dates
      setQuickCheckEndDate('');
      
      // Fetch current rentals for this dress
      await fetchCurrentRentals(data.id);
      await fetchRentalHistory(data.id);
      
      success('Dress found successfully!');
    } catch (error: any) {
      showError('Error searching dress: ' + error.message);
      setDress(null);
      setRentalAvailability(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentRentals = async (dressId: string) => {
    try {
      const { data, error } = await supabase
        .from('rentals')
        .select(`
          *,
          customers (
            id,
            name,
            email,
            phone
          )
        `)
        .eq('dress_id', dressId)
        .eq('status', 'active')
        .order('rental_date', { ascending: false });

      if (error) throw error;
      setCurrentRentals(data || []);
    } catch (error: any) {
      showError('Error fetching current rentals: ' + error.message);
    }
  };

  const fetchRentalHistory = async (dressId: string) => {
    try {
      const { data, error } = await supabase
        .from('rentals')
        .select(`
          *,
          customers (
            id,
            name,
            email,
            phone
          )
        `)
        .eq('dress_id', dressId)
        .neq('status', 'active')
        .order('rental_date', { ascending: false })
        .limit(10);

      if (error) throw error;
      setRentalHistory(data || []);
    } catch (error: any) {
      showError('Error fetching rental history: ' + error.message);
    }
  };

  const handleScannerInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setScannerInput(value);
    
    // Auto-search when input stops for 500ms
    setTimeout(() => {
      if (value.length >= 8) {
        searchDressByBarcode(value);
        setScannerInput('');
      }
    }, 500);
  };

  const handleManualSearch = () => {
    searchDressByBarcode(barcode);
  };

  const handleCreateRental = async (values: Record<string, any>) => {
    if (!dress) return;

    try {
      // Check availability before creating rental
      const availability = await checkDressAvailability(
        dress.id,
        values.rental_date,
        values.expected_return_date
      );

      if (!availability.isAvailable) {
        const conflictDetails = availability.conflictingRentals
          .map(r => `${r.customer_name} (${new Date(r.rental_date).toLocaleDateString()} - ${new Date(r.expected_return_date).toLocaleDateString()})`)
          .join(', ');
        
        showError(`Dress is not available for the selected dates. Conflicts with: ${conflictDetails}`);
        setRentalAvailability(availability);
        return;
      }

      const { data: newRental, error } = await supabase
        .from('rentals')
        .insert({
          dress_id: dress.id,
          customer_id: values.customer_id,
          rental_date: values.rental_date,
          expected_return_date: values.expected_return_date,
          rental_price: values.rental_price || dress.rental_price,
          deposit_amount: values.deposit_amount || 0,
          location: values.location,
          alterations: values.alterations || null,
          status: 'active',
          notes: values.notes
        })
        .select(`
          *,
          dresses (
            id,
            name,
            barcode,
            color,
            size,
            rental_price,
            photo_url
          ),
          customers (
            id,
            name,
            email,
            phone
          )
        `)
        .single();

      if (error) throw error;

      success('Rental created successfully!');
      setRentalModalVisible(false);
      setRentalAvailability(null);
      
      // Auto-open contract modal for new rental
      setSelectedRental(newRental);
      setShowContract(true);
      
      // Refresh data
      await fetchCurrentRentals(dress.id);
      await fetchRentalHistory(dress.id);
    } catch (error: any) {
      showError('Error creating rental: ' + error.message);
    }
  };


  const handleCustomerAdded = (newCustomer: Customer) => {
    setCustomers(prev => [...prev, newCustomer]);
  };

  const handleCloseContract = () => {
    setShowContract(false);
    setSelectedRental(null);
  };

  const rentalColumns: Column<Rental>[] = [
    {
      key: 'customer',
      title: 'Customer',
      render: (_, record: Rental) => (
        <div>
          <div className="font-semibold">{(record as any).customers?.name}</div>
          <div className="text-sm text-gray-500">{(record as any).customers?.email}</div>
        </div>
      ),
    },
    {
      key: 'rental_date',
      title: 'Rental Date',
      dataIndex: 'rental_date',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      key: 'expected_return_date',
      title: 'Expected Return',
      dataIndex: 'expected_return_date',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      render: (status: string) => {
        const colors = { active: 'blue', returned: 'green', overdue: 'red' } as const;
        return <Tag color={colors[status as keyof typeof colors] || 'gray'}>{status}</Tag>;
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <QrCodeIcon className="h-6 w-6 mr-2" />
          Barcode Scanner
        </h1>
        <p className="text-gray-600 mt-1">
          Scan or search for dress barcodes to view details and manage rentals
        </p>
      </div>

      {/* Scanner Section */}
      <Card title="Barcode Scanner" className="bg-blue-50">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Scanner Input (focus here for barcode scanning)
            </label>
            <Input
              ref={scannerInputRef}
              value={scannerInput}
              onChange={handleScannerInput}
              onBlur={handleScannerInputBlur}
              placeholder="Scan barcode here..."
              className="font-mono text-lg"
              autoFocus
            />
          </div>
          
          <div className="border-t pt-4 manual-search-container">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Manual Search
            </label>
            <div className="flex space-x-2">
              <Input
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                placeholder="Enter barcode manually..."
                className="flex-1"
                onKeyPress={(e) => e.key === 'Enter' && handleManualSearch()}
              />
              <Button
                variant="primary"
                onClick={handleManualSearch}
                loading={loading}
                className="flex items-center"
              >
                <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                Search
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Dress Details */}
      {dress && (
        <Card title="Dress Details">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900">{dress.name}</h3>
                  <p className="text-gray-600">{(dress as any).brands?.name || 'No Brand'}</p>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-green-600">${dress.rental_price}</span>
                  <p className="text-sm text-gray-500">Rental Price</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Category</p>
                  <Tag color={(dress as any).categories?.color || 'blue'}>
                    {(dress as any).categories?.name || 'No Category'}
                  </Tag>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Color</p>
                  <Tag color="purple">{dress.color}</Tag>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Size</p>
                  <Tag color="gray">{dress.size}</Tag>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Barcode</p>
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">{dress.barcode}</code>
                </div>
              </div>

              {dress.description && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Description</p>
                  <p className="text-gray-700">{dress.description}</p>
                </div>
              )}

              <div className="flex space-x-3">
                <Button
                  variant="primary"
                  onClick={() => setRentalModalVisible(true)}
                  className="flex items-center"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create Rental
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setHistoryModalVisible(true)}
                  className="flex items-center"
                >
                  <ClockIcon className="h-4 w-4 mr-2" />
                  View History
                </Button>
                {dress.photo_url && (
                  <Button
                    variant="outline"
                    onClick={() => setImageModalVisible(true)}
                    className="flex items-center"
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    View Photo
                  </Button>
                )}
              </div>
            </div>

            <div>
              {dress.photo_url ? (
                <Image
                  src={dress.photo_url}
                  alt={dress.name}
                  className="w-full rounded-lg"
                  preview
                />
              ) : (
                <div className="w-full aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                  <p className="text-gray-500">No photo available</p>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Quick Availability Checker */}
      {dress && (
        <Card title="Quick Availability Check">
          <div 
            className="space-y-4" 
            id="availability-checker"
            onMouseEnter={() => setIsUsingAvailabilityChecker(true)}
            onMouseLeave={() => setIsUsingAvailabilityChecker(false)}
          >
            <p className="text-gray-600 text-sm">
              Check availability for this dress by selecting your desired rental period
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Date *
                </label>
                <DatePicker 
                  placeholder="Select start date"
                  value={quickCheckStartDate}
                  onChange={(date) => {
                    setQuickCheckStartDate(date || '');
                  }}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To Date *
                </label>
                <DatePicker 
                  placeholder="Select end date"
                  value={quickCheckEndDate}
                  onChange={(date) => {
                    setQuickCheckEndDate(date || '');
                  }}
                />
              </div>
            </div>
            
            {/* Quick Availability Results */}
            {rentalAvailability && !rentalAvailability.isAvailable && (
              <div className="bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="bg-red-500 rounded-full p-1.5 flex-shrink-0">
                    <ExclamationTriangleIcon className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-red-900 text-sm mb-2">
                      ❌ Not Available
                    </h4>
                    <div className="space-y-2">
                      {rentalAvailability.conflictingRentals.map((conflict, index) => (
                        <div key={index} className="bg-white border border-red-200 rounded p-2 text-xs">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-red-900">👤 {conflict.customer_name}</p>
                              <p className="text-red-700">
                                📅 {new Date(conflict.rental_date).toLocaleDateString('en-US', { 
                                  month: 'short', day: 'numeric' 
                                })} → {new Date(conflict.expected_return_date).toLocaleDateString('en-US', { 
                                  month: 'short', day: 'numeric' 
                                })}
                              </p>
                            </div>
                            <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs">
                              Booked
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {rentalAvailability.nextAvailableDate && (
                      <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-3">
                        <p className="text-blue-900 text-sm">
                          💡 <span className="font-medium">Available from:</span>{' '}
                          <span className="font-bold">
                            {new Date(rentalAvailability.nextAvailableDate).toLocaleDateString('en-US', { 
                              weekday: 'short', month: 'short', day: 'numeric' 
                            })}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {rentalAvailability && rentalAvailability.isAvailable && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-500 rounded-full p-1.5">
                      <CheckCircleIcon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-green-900 text-sm">
                        ✅ Available!
                      </h4>
                      <p className="text-green-800 text-xs">
                        This dress is free for your selected dates
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setRentalModalVisible(true)}
                    className="flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Book Now
                  </Button>
                </div>
              </div>
            )}
            
            {!rentalAvailability && (
              <div className="text-center py-4 text-gray-500 text-sm">
                Select both dates to check availability
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Current Rentals - Compact View */}
      {dress && currentRentals.length > 0 && (
        <Card title={`Current Rentals (${currentRentals.length})`}>
          <div className="space-y-2">
            {currentRentals.map((rental) => (
              <div key={rental.id} className="bg-gray-50 rounded-lg p-3 border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      👤 {(rental as any).customers?.name}
                    </p>
                    <p className="text-gray-600 text-xs">
                      📅 {new Date(rental.rental_date).toLocaleDateString()} → {new Date(rental.expected_return_date).toLocaleDateString()}
                    </p>
                  </div>
                  <Tag color="blue" className="text-xs">
                    {rental.status}
                  </Tag>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Create Rental Modal */}
      <Modal
        isOpen={rentalModalVisible}
        onClose={() => {
          setRentalModalVisible(false);
          setRentalAvailability(null);
        }}
        title="Create New Rental"
        size="lg"
      >
        <Form onSubmit={handleCreateRental}>
          <FormItem name="customer_id" label="Customer" required>
            <CustomerSelect
              placeholder="Select customer or add new"
              onCustomerAdded={handleCustomerAdded}
            />
          </FormItem>

          <div className="grid grid-cols-2 gap-4">
            <FormItem name="rental_date" label="Rental Date" required>
              <DatePicker 
                placeholder="Select rental date"
                onChange={async (date) => {
                  if (!dress || !date) return;
                  // Get form data to check availability when both dates are available
                  const form = document.querySelector('form');
                  if (form) {
                    const formData = new FormData(form);
                    const returnDate = formData.get('expected_return_date');
                    if (returnDate) {
                      const availability = await checkDressAvailability(dress.id, date, returnDate as string);
                      setRentalAvailability(availability);
                    }
                  }
                }}
              />
            </FormItem>

            <FormItem name="expected_return_date" label="Expected Return Date" required>
              <DatePicker 
                placeholder="Select return date"
                onChange={async (date) => {
                  if (!dress || !date) return;
                  // Get form data to check availability when both dates are available
                  const form = document.querySelector('form');
                  if (form) {
                    const formData = new FormData(form);
                    const rentalDate = formData.get('rental_date');
                    if (rentalDate) {
                      const availability = await checkDressAvailability(dress.id, rentalDate as string, date);
                      setRentalAvailability(availability);
                    }
                  }
                }}
              />
            </FormItem>
          </div>

          {/* Availability Warning - Improved Design */}
          {rentalAvailability && !rentalAvailability.isAvailable && (
            <div className="bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 rounded-lg p-6 shadow-sm">
              <div className="flex items-start space-x-4">
                <div className="bg-red-500 rounded-full p-2 flex-shrink-0">
                  <ExclamationTriangleIcon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-red-900 mb-3">
                    ⚠️ Booking Conflict Detected
                  </h3>
                  <p className="text-red-800 mb-4 leading-relaxed">
                    Sorry! This dress is already rented during your selected dates. Here are the conflicting bookings:
                  </p>
                  
                  <div className="space-y-3 mb-4">
                    {rentalAvailability.conflictingRentals.map((conflict, index) => (
                      <div key={index} className="bg-white border border-red-200 rounded-lg p-4 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-red-900 text-base">
                              👤 {conflict.customer_name}
                            </p>
                            <p className="text-red-700 text-sm mt-1">
                              📅 {new Date(conflict.rental_date).toLocaleDateString('en-US', { 
                                weekday: 'short', 
                                month: 'short', 
                                day: 'numeric' 
                              })} 
                              → 
                              {new Date(conflict.expected_return_date).toLocaleDateString('en-US', { 
                                weekday: 'short', 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </p>
                          </div>
                          <div className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                            Active Rental
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {rentalAvailability.nextAvailableDate && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-blue-900 font-medium">
                        💡 <span className="font-semibold">Suggestion:</span> This dress will be available starting{' '}
                        <span className="font-bold text-blue-800">
                          {new Date(rentalAvailability.nextAvailableDate).toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Availability Confirmation - Improved Design */}
          {rentalAvailability && rentalAvailability.isAvailable && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-lg p-6 shadow-sm">
              <div className="flex items-center space-x-4">
                <div className="bg-green-500 rounded-full p-2 flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-green-900 mb-1">
                    ✅ Perfect! Dates Available
                  </h3>
                  <p className="text-green-800">
                    This dress is completely free for your selected rental period. You're good to go!
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <FormItem name="rental_price" label="Rental Price">
              <div className="space-y-2">
                <Input
                  type="number"
                  step="0.01"
                  value={dress?.rental_price || ''}
                  placeholder="0.00"
                  readOnly
                  className="bg-gray-50"
                />
                <p className="text-sm text-gray-600">
                  Price automatically set from dress: <span className="font-medium">${dress?.rental_price || '0.00'}</span>
                </p>
              </div>
            </FormItem>

            <FormItem name="deposit_amount" label="Deposit Amount" required>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
              />
            </FormItem>
          </div>

          <FormItem name="location" label="Location (Event Location)" required>
            <Input
              placeholder="Where is the dress being rented for?"
            />
          </FormItem>

          <FormItem name="alterations" label="Dress Alterations">
            <div className="space-y-2">
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="alterations"
                    value="CLIENT"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">CLIENT</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="alterations"
                    value="DMU"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">DMU</span>
                </label>

              </div>
            </div>
          </FormItem>

          <FormItem name="notes" label="Notes">
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Additional notes..."
            />
          </FormItem>

          <div className="flex justify-end space-x-3 mt-6">
            <Button variant="outline" onClick={() => setRentalModalVisible(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Create Rental
            </Button>
          </div>
        </Form>
      </Modal>

      {/* History Modal */}
      <Modal
        isOpen={historyModalVisible}
        onClose={() => setHistoryModalVisible(false)}
        title="Rental History"
        size="xl"
      >
        <Table
          columns={rentalColumns}
          dataSource={rentalHistory}
          rowKey="id"
          pagination={{
            current: 1,
            pageSize: 10,
            total: rentalHistory.length,
            onChange: () => {}
          }}
        />
      </Modal>

      {/* Image Modal */}
      {dress?.photo_url && (
        <Modal
          isOpen={imageModalVisible}
          onClose={() => setImageModalVisible(false)}
          title={dress.name}
          size="lg"
        >
          <div className="text-center">
            <Image
              src={dress.photo_url}
              alt={dress.name}
              className="max-w-full h-auto"
            />
          </div>
        </Modal>
      )}

      {/* Rental Contract Modal */}
      {selectedRental && (
        <RentalContract
          rental={selectedRental}
          isOpen={showContract}
          onClose={handleCloseContract}
        />
      )}
    </div>
  );
};

export default BarcodeScanner; 